import { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { endpoint } from "../../api/endpoint";
import { apiHandler } from "../../api";
import { useBackgroundUpload } from "../BackgroundSelector/useBackgroundUpload";
import {
  deletePlaygroundItem,
  getPlaygroundItemLabel,
  resolvePlaygroundItemId,
  showDeleteConfirmationToast,
} from "../../utils/playgroundDelete";

const DEFAULT_PAGE_LIMIT = 10;

const normalizeBackgroundItem = (item) => {
  if (!item) return null;
  const activeFlag =
    item?.bIsActive !== undefined
      ? Boolean(item.bIsActive && item.bIsActive !== "0" && item.bIsActive !== 0)
      : item?.isActive !== undefined
        ? Boolean(item.isActive && item.isActive !== "0" && item.isActive !== 0)
        : item?.status !== undefined
          ? Boolean(item.status === true || item.status === 1 || item.status === "1" || item.status === "active")
          : true;

  const id = resolvePlaygroundItemId(item) || item.id || item.iBackgroundId || item.backgroundId;
  const images = Array.isArray(item.images) && item.images.length > 0
    ? item.images.filter(Boolean)
    : [item.front || item.image || item.sImage, item.back].filter(Boolean);

  const tierVal = item.tier || item.sTier || "";
  const sourceVal = item.source || (tierVal.toLowerCase().includes("ai") ? "ai" : "upload");

  return {
    ...item,
    id,
    backgroundId: id,
    name: item.name || item.title || item.sTitle || "Studio Background",
    front: item.front || images[0] || "",
    back: item.back || images[1] || "",
    image: item.image || images[0] || "",
    images: images.length > 0 ? images : [""],
    tier: tierVal,
    source: sourceVal,
    bIsActive: activeFlag,
    isActive: activeFlag,
  };
};

export const useBackgroundListing = () => {
  const { role: userRole, token } = useSelector((state) => state.auth || {});

  const [gender, setGender] = useState("women");
  const [source, setSource] = useState("all"); // "all" | "upload" | "ai"
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_PAGE_LIMIT);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [backgrounds, setBackgrounds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [backgroundToEdit, setBackgroundToEdit] = useState(null);

  // Zoom modal
  const [zoomModal, setZoomModal] = useState({ open: false, front: "", back: "" });

  const {
    uploadBackground,
    updateBackground,
    isLoading: isUploadLoading,
  } = useBackgroundUpload();

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleGenderChange = (newGender) => {
    setGender(newGender);
    setPage(1);
  };

  const handleSourceChange = (newSource) => {
    setSource(newSource);
    setPage(1);
  };

  const fetchBackgrounds = useCallback(async () => {
    if (!token) return;

    try {
      setLoading(true);
      setError(null);

      const queryObj = {
        gender,
        search: debouncedSearch,
        page,
        limit,
      };

      if (source && source !== "all") {
        queryObj.source = source;
      }

      const query = new URLSearchParams(queryObj);

      const response = await apiHandler({
        url: `${endpoint.API_BASE_URL}${endpoint.GET_BACKGROUNDS}?${query.toString()}`,
        method: "GET",
        authToken: { token },
        noCache: true,
      });

      const payload = response?.data;
      const rows = Array.isArray(payload?.data?.rows)
        ? payload.data.rows
        : Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload?.rows)
            ? payload.rows
            : Array.isArray(payload)
              ? payload
              : [];

      let normalized = rows.map(normalizeBackgroundItem).filter(Boolean);

      // Client-side fallback filter if backend didn't filter by source
      if (source && source !== "all") {
        const sourceMatches = normalized.filter((item) => item.source === source);
        if (sourceMatches.length > 0 || normalized.some((item) => item.source && item.source !== source)) {
          normalized = sourceMatches;
        }
      }

      setBackgrounds(normalized);

      const metaTotal = Number(payload?.data?.total ?? payload?.total ?? normalized.length) || 0;
      const metaPages = Math.max(1, Number(payload?.data?.totalPages ?? payload?.totalPages ?? Math.ceil(metaTotal / limit)) || 1);

      setTotal(metaTotal);
      setTotalPages(metaPages);
    } catch (err) {
      console.error("Error fetching backgrounds:", err);
      setError(err?.message || "Failed to load backgrounds.");
      setBackgrounds([]);
      setTotal(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [token, gender, source, debouncedSearch, page, limit]);

  useEffect(() => {
    fetchBackgrounds();
  }, [fetchBackgrounds]);

  // Status Toggle
  const handleToggleBackground = async (bg) => {
    const itemId = resolvePlaygroundItemId(bg);
    if (!itemId) {
      toast.error("Background ID not found.");
      return;
    }

    const previousState = Boolean(bg.bIsActive);
    const newState = !previousState;

    // Optimistic UI update
    setBackgrounds((prev) =>
      prev.map((item) =>
        resolvePlaygroundItemId(item) === itemId
          ? { ...item, bIsActive: newState, isActive: newState }
          : item
      )
    );

    try {
      const response = await apiHandler({
        url: `${endpoint.TOGGLE_BACKGROUND}/${itemId}/toggle`,
        method: "PATCH",
        authToken: { token },
        noCache: true,
      });

      if (
        (response?.status && response.status >= 400) ||
        response?.data?.status === "error" ||
        response?.data?.success === false
      ) {
        throw new Error(response?.data?.message || "Failed to update status");
      }

      toast.success(`Background ${newState ? "activated" : "deactivated"} successfully.`);
    } catch (err) {
      console.error("Background toggle error:", err);
      toast.error(err?.message || "Failed to update background status.");
      // Rollback
      setBackgrounds((prev) =>
        prev.map((item) =>
          resolvePlaygroundItemId(item) === itemId
            ? { ...item, bIsActive: previousState, isActive: previousState }
            : item
        )
      );
    }
  };

  // Delete Background
  const handleDeleteBackground = (bg) => {
    const itemId = resolvePlaygroundItemId(bg);
    if (!itemId) {
      toast.error("Background missing an ID.");
      return;
    }

    showDeleteConfirmationToast({
      title: "Delete background?",
      description: `This will permanently remove ${getPlaygroundItemLabel(bg)} from the catalog.`,
      confirmLabel: "Delete background",
      onConfirm: async () => {
        try {
          const result = await deletePlaygroundItem({
            resource: "background",
            itemId,
            token,
          });

          if (result?.status >= 200 && result?.status < 300) {
            toast.success("Background deleted successfully.");
            setBackgrounds((prev) => prev.filter((item) => resolvePlaygroundItemId(item) !== itemId));
            setTotal((prev) => Math.max(0, prev - 1));
            return;
          }
          toast.error(result?.data?.error_message || result?.data?.error || "Failed to delete background.");
        } catch (error) {
          toast.error(error?.message || "Failed to delete background.");
        }
      },
    });
  };

  // Upload/Edit Submit (supports both (payload, isEdit, backgroundId) and (payload, itemToEdit))
  const handleUploadSubmit = async (payload, isEditOrItem, maybeId) => {
    try {
      const isEdit = Boolean(isEditOrItem);
      const targetId =
        maybeId ||
        (typeof isEditOrItem === "object" ? resolvePlaygroundItemId(isEditOrItem) : null) ||
        resolvePlaygroundItemId(backgroundToEdit) ||
        backgroundToEdit?.id ||
        backgroundToEdit?.iBackgroundId ||
        backgroundToEdit?.backgroundId;

      if (isEdit && targetId) {
        const result = await updateBackground(targetId, payload);
        if (result.success) {
          toast.success("Background updated successfully!");
          handleCloseModal();
          fetchBackgrounds();
        } else {
          toast.error(`Update failed: ${result.error}`);
        }
      } else if (isEdit && !targetId) {
        toast.error("Unable to update background: Background ID is missing.");
      } else {
        const result = await uploadBackground(payload);
        if (result.success) {
          toast.success("Background uploaded successfully!");
          handleCloseModal();
          fetchBackgrounds();
        } else {
          toast.error(`Upload failed: ${result.error}`);
        }
      }
    } catch (err) {
      toast.error(`Error: ${err.message}`);
    }
  };

  const handleOpenUploadModal = () => {
    setBackgroundToEdit(null);
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (bg) => {
    setBackgroundToEdit(bg);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setBackgroundToEdit(null);
  };

  const openZoomModal = (front, back = "") => {
    setZoomModal({ open: true, front, back });
  };

  const closeZoomModal = () => {
    setZoomModal({ open: false, front: "", back: "" });
  };

  return {
    userRole,
    gender,
    onGenderChange: handleGenderChange,
    source,
    onSourceChange: handleSourceChange,
    searchTerm,
    onSearchChange: (e) => setSearchTerm(e?.target ? e.target.value : e),
    backgrounds,
    loading,
    error,
    refreshBackgrounds: fetchBackgrounds,
    pagination: {
      page,
      limit,
      total,
      totalPages,
    },
    onPageChange: (newPage) => setPage(newPage),
    onLimitChange: (newLimit) => {
      setLimit(Number(newLimit) || 10);
      setPage(1);
    },
    handleToggleBackground,
    handleDeleteBackground,
    addBackgroundLocally: (newBg) => {
      setBackgrounds((prev) => [normalizeBackgroundItem(newBg), ...prev]);
      setTotal((prev) => prev + 1);
    },
    // Modals
    isModalOpen,
    backgroundToEdit,
    isUploadLoading,
    handleOpenUploadModal,
    handleOpenEditModal,
    handleCloseModal,
    handleUploadSubmit,
    // Zoom
    zoomModal,
    openZoomModal,
    closeZoomModal,
  };
};
